<template>
  <v-dialog :model-value="modelValue" max-width="760px" @update:model-value="handleDialogToggle">
    <v-card>
      <v-card-title class="info-title">
        {{ isEdit ? $t("companies.dialog.editTitle") : $t("companies.dialog.addTitle") }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-alert
          v-if="errorMessage"
          type="error"
          density="comfortable"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>
        <v-form @submit.prevent="handleSave">
          <v-container class="pa-0">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.company_name"
                  :label="$t('companies.form.name')"
                  :rules="[requiredRule]"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.administrator"
                  :label="$t('companies.form.administrator')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.fiscal_code"
                  :label="$t('companies.form.fiscalCode')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.vat_number"
                  :label="$t('companies.form.vat')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.phone"
                  :label="$t('companies.form.phone')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.email"
                  :label="$t('companies.form.email')"
                  type="email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.address"
                  :label="$t('companies.form.address')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.postal_code"
                  :label="$t('companies.form.postalCode')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.iban"
                  :label="$t('companies.form.iban')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.contact_person"
                  :label="$t('companies.form.contactPerson')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="localForm.status"
                  :label="$t('companies.form.status')"
                  :items="statusItems"
                  item-title="title"
                  item-value="value"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="localForm.pdfrequest"
                  :true-value="1"
                  :false-value="0"
                  :label="$t('companies.form.pdfrequest')"
                  density="compact"
                  hide-details
                ></v-checkbox>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="localForm.email_notifications_disabled"
                  :true-value="1"
                  :false-value="0"
                  :label="$t('companies.form.emailNotificationsDisabled')"
                  density="compact"
                  hide-details
                ></v-checkbox>
              </v-col>

              <v-col cols="12" class="pt-2">
                <div class="text-subtitle-2 mb-2">{{ $t("companies.form.licenseSection") }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="localForm.license_package_key"
                  :label="$t('companies.form.licensePackage')"
                  :items="licensePackageItems"
                  :loading="licensePackagesLoading"
                  item-title="title"
                  item-value="value"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localForm.license_expires_at"
                  :label="$t('companies.form.licenseExpiresAt')"
                  :hint="$t('companies.form.licenseExpiresHint')"
                  persistent-hint
                  type="date"
                ></v-text-field>
              </v-col>

              <v-col cols="12" class="dialog-actions">
                <v-btn variant="text" :disabled="saveLoading" @click="closeDialog">
                  {{ $t("app.actions.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn color="primary" type="submit" :loading="saveLoading">
                  {{ $t("app.actions.save") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from "vue";
import { createEmptyCompanyForm } from "@/Utile/companyForm.mjs";

export default defineComponent({
  name: "CompanyFormDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    formValue: {
      type: Object,
      default: () => createEmptyCompanyForm(),
    },
    statusItems: {
      type: Array,
      default: () => [],
    },
    licensePackageItems: {
      type: Array,
      default: () => [],
    },
    licensePackagesLoading: {
      type: Boolean,
      default: false,
    },
    saveLoading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
  },
  emits: ["save", "update:modelValue"],
  data() {
    return {
      localForm: createEmptyCompanyForm(),
    };
  },
  computed: {
    requiredRule() {
      return (value) => !!value || this.$t("companies.form.required");
    },
  },
  watch: {
    formValue: {
      immediate: true,
      deep: true,
      handler() {
        this.syncLocalForm();
      },
    },
    modelValue(newValue) {
      if (newValue) {
        this.syncLocalForm();
      }
    },
  },
  methods: {
    syncLocalForm() {
      this.localForm = {
        ...createEmptyCompanyForm(),
        ...(this.formValue || {}),
      };
    },
    handleDialogToggle(value) {
      if (!value) {
        this.closeDialog();
        return;
      }
      this.$emit("update:modelValue", value);
    },
    closeDialog() {
      if (this.saveLoading) return;
      this.$emit("update:modelValue", false);
    },
    handleSave() {
      this.$emit("save", { ...this.localForm });
    },
  },
});
</script>

<style scoped>
.dialog-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}
</style>
